/**
 * The staging strip (#1069 Story 4.1, Task 2, AC 2 and AC 3).
 *
 * What the person has chosen but not yet sent, sitting BETWEEN the text field
 * and the actions row. Not inside the `TextInput` (React Native cannot reliably
 * inline non-text views into a text input across web, iOS and Android) and not
 * below the actions row (the panel has a logged hazard where Post gets pushed
 * past the dock edge, and Post must stay the last thing before it).
 *
 * ⚠️ REMOVAL TRAVELS BY ID, NEVER BY NAME OR INDEX. Two files called
 * `photo.pdf` are legal and both must stage, so the name cannot identify one.
 * An index would be worse: it shifts under every removal.
 *
 * ⚠️ WHILE SENDING, THE FILES STAY AND ONLY THE CONTROLS GO (AC 12). Not
 * cleared, not faded. Clearing optimistically would lose file handles the
 * failure path cannot re-derive, and AC 13 promises the message and its files
 * are still there after a failure.
 */
import React from 'react';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';

import { isPreviewableImage } from '../../services/correspondence/stagedFile';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { X } from '../icons';

import type { StagedFile } from '../../services/correspondence/stagedFile';

/**
 * The limits, stated (AC 3, D2). Shown only once something is staged, so it
 * appears when it is relevant instead of sitting under an empty composer
 * forever and becoming furniture.
 *
 * ⚠️ THESE NUMBERS ARE AN ACCEPTANCE CRITERION, not a detail: 6 files, 25 MB
 * each (D1 as amended 2026-09-13), 40 MB per message. If a ceiling moves, this
 * string and `messageFileLimits.ts` move together or the composer lies.
 */
export const STAGED_LIMITS_HINT = 'Up to 6 files · 25 MB each · 40 MB total';

/**
 * After a cancel that MIGHT have committed (owner decision, code review run 3,
 * 2026-09-13).
 *
 * ⚠️ `xhr.abort()` TEARS DOWN THE CONNECTION; IT DOES NOT UNDO THE WRITE. A
 * cancel at 99% can land after the server has committed, and AC 12 deliberately
 * keeps the strip — so the committed message appears in the thread while its
 * exact files sit in the composer, indistinguishable from files that never
 * went. One press of Post sent everything twice. The idempotency key is the
 * general answer and is deferred (no precedent in this product, and it means a
 * column, a unique index and a replay store); until it exists, the honest thing
 * is to SAY SO rather than guess, which is why this is a line on the strip and
 * not an automatic clear: clearing on a heuristic would throw away files that
 * never went.
 */
export const MAY_ALREADY_BE_SENT =
  'These files may already have been sent. Check the conversation before posting again.';

type Props = {
  files: readonly StagedFile[];
  onRemove: (id: string) => void;
  /** In flight: keep the files, drop the controls (AC 12). */
  sending?: boolean;
  /** Show `MAY_ALREADY_BE_SENT` above the hint. */
  mayAlreadyBeSent?: boolean;
};

/** The 20x20 `×`, hung off the top-trailing corner of a tile or a chip. */
const RemoveControl = ({
  file,
  onRemove,
}: {
  file: StagedFile;
  onRemove: (id: string) => void;
}) => (
  <TouchableOpacity
    onPress={() => onRemove(file.id)}
    style={styles.remove}
    // 20x20 is under the 44pt target, so the hit area is widened rather than
    // the control: the visual needs to stay off the corner of a 56pt tile.
    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    accessibilityRole="button"
    accessibilityLabel={`Remove ${file.name}`}
  >
    <X size={12} color={colors.textSecondary} />
  </TouchableOpacity>
);

export const StagedFileStrip = ({
  files,
  onRemove,
  sending,
  mayAlreadyBeSent,
}: Props) => {
  // Nothing staged is nothing rendered, hint included (AC 3).
  if (files.length === 0) return null;

  const images = files.filter(f => isPreviewableImage(f.mimeType));
  const documents = files.filter(f => !isPreviewableImage(f.mimeType));

  return (
    <View style={styles.strip} testID="correspondence-staged-files">
      {images.length > 0 ? (
        <View style={styles.tiles}>
          {images.map(file => (
            <View
              key={file.id}
              style={styles.tile}
              testID={`staged-tile-${file.id}`}
            >
              <Image
                source={{ uri: file.previewUri }}
                style={styles.tileImage}
                // The name, so a screen reader is not told "image".
                accessibilityLabel={file.name}
              />
              {sending ? null : (
                <RemoveControl file={file} onRemove={onRemove} />
              )}
            </View>
          ))}
        </View>
      ) : null}

      {documents.map(file => (
        <View
          key={file.id}
          style={styles.chip}
          testID={`staged-chip-${file.id}`}
        >
          <Text style={styles.chipName} numberOfLines={1}>
            {file.name}
          </Text>
          {sending ? null : <RemoveControl file={file} onRemove={onRemove} />}
        </View>
      ))}

      {mayAlreadyBeSent ? (
        <Text style={styles.warning} testID="correspondence-staged-may-be-sent">
          {MAY_ALREADY_BE_SENT}
        </Text>
      ) : null}

      <Text style={styles.hint}>{STAGED_LIMITS_HINT}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  strip: {
    width: '100%',
    gap: spacing.sm,
    paddingTop: spacing.sm,
  },
  tiles: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  tile: {
    width: 56,
    height: 56,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.borderPanelSoft,
    overflow: 'visible',
  },
  tileImage: {
    width: '100%',
    height: '100%',
    borderRadius: 6,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    height: 32,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.borderPanelSoft,
    paddingHorizontal: spacing.sm,
  },
  chipName: {
    flex: 1,
    fontSize: 12,
    color: colors.textPrimary,
  },
  remove: {
    position: 'absolute',
    top: -4,
    right: -4,
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderPanelSoft,
  },
  hint: { fontSize: 11, color: colors.textOnWarningMuted },
  // The same scale as the hint it sits above, in the panel's own error colour:
  // this is a caution about what already happened, not a refusal of anything.
  warning: { fontSize: 11, color: colors.error },
});
